
if (!customElements.get('visual-feature-toggle')) {

  const DEFAULT_STATE = 'on';
  const SWITCH_AT = 0.5;

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  class VisualFeatureToggle extends HTMLElement {

    connectedCallback() {

      // Elements
      this.stage = this.closest(
        '[data-visual-feature-stage]'
      );

      this.stickyWrap = this.closest(
        '.visual-feature__sticky-wrap'
      );

      this.buttons = Array.from(
        this.querySelectorAll(
          '[data-visual-feature-toggle-btn]'
        )
      );

      this.stateItems = Array.from(
        this.querySelectorAll(
          '[data-visual-feature-media], ' +
          '[data-visual-feature-caption], ' +
          '[data-visual-feature-icon]'
        )
      );

      // State
      this.currentState = null;
      this.ticking = false;
      this.lastScrollY = window.scrollY;

      // Bind methods
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      // Button interactions
      this.clickHandlers = this.buttons.map(button => {

        const handler = () => {
          const state = button.dataset.state;

          this.setState(state);
        };

        button.addEventListener('click', handler);

        return handler;
      });

      // Set initial state
      this.setState(DEFAULT_STATE);

      // Scroll interactions
      if (this.stage && !reducedMotion.matches) {

        window.addEventListener(
          'scroll',
          this.onScroll,
          { passive: true }
        );

        window.addEventListener(
          'resize',
          this.onResize
        );

        // Account for initial page position
        requestAnimationFrame(() => {
          if (this.isConnected) {
            this.updateFromScroll();
          }
        });
      }
    }


    /* =====================================
       SCROLL HANDLER
    ===================================== */

    onScroll() {

      const currentY = window.scrollY;

      // Ignore events without meaningful movement
      if (Math.abs(currentY - this.lastScrollY) < 2) {
        return;
      }

      this.lastScrollY = currentY;

      if (this.ticking) return;

      this.ticking = true;

      requestAnimationFrame(() => {

        if (this.isConnected) {
          this.updateFromScroll();
        }

        this.ticking = false;
      });
    }


    /* =====================================
       RESIZE HANDLER
    ===================================== */

    onResize() {

      requestAnimationFrame(() => {

        if (this.isConnected) {
          this.updateFromScroll();
        }

      });
    }


    /* =====================================
       SCROLL PROGRESS
    ===================================== */

    updateFromScroll() {

      if (!this.stage || !this.stickyWrap) return;

      const stageRect =
        this.stage.getBoundingClientRect();

      const stageHeight =
        this.stage.offsetHeight;

      const stickyHeight =
        this.stickyWrap.offsetHeight;

      // Total distance over which the
      // sticky content travels with the stage
      const scrollDistance = Math.max(
        stageHeight - stickyHeight,
        1
      );

      // Progress starts when the stage
      // reaches the top of the viewport
      const rawProgress =
        -stageRect.top / scrollDistance;

      const progress = Math.min(
        Math.max(rawProgress, 0),
        1
      );

      // Switch at 50%
      const state =
        progress >= SWITCH_AT
          ? 'off'
          : 'on';

      this.setState(state);
    }


    /* =====================================
       STATE MANAGEMENT
    ===================================== */

    setState(state) {

      if (state !== 'on' && state !== 'off') {
        return;
      }

      this.currentState = state;

      // Update buttons
      this.buttons.forEach(button => {

        const active =
          button.dataset.state === state;

        button.classList.toggle(
          'is-active',
          active
        );

        button.setAttribute(
          'aria-pressed',
          String(active)
        );
      });

      // Update media, captions and icons
      this.stateItems.forEach(item => {

        const active =
          item.dataset.state === state;

        item.classList.toggle(
          'is-active',
          active
        );
      });
    }


    /* =====================================
       CLEANUP
    ===================================== */

    disconnectedCallback() {

      window.removeEventListener(
        'scroll',
        this.onScroll
      );

      window.removeEventListener(
        'resize',
        this.onResize
      );

      if (this.clickHandlers) {

        this.buttons.forEach((button, index) => {

          button.removeEventListener(
            'click',
            this.clickHandlers[index]
          );

        });
      }
    }
  }

  customElements.define(
    'visual-feature-toggle',
    VisualFeatureToggle
  );
}
